import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Users as UsersIcon, Pencil, Search, Eye } from 'lucide-react';
import { formatDate } from '@/shared/lib/utils';
import type { User } from '@/shared/types';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Pagination } from '@travellegacy/nexops-design-system';
import { ClientModal } from '../components/ClientModal';
import { toast } from '@/shared/lib/toast';
import { useAuth } from '@/shared/contexts/AuthContext';
import { isAxiosError } from 'axios';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  SortableTable,
} from '@travellegacy/nexops-design-system';

export default function Clients() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { impersonate } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await usersAPI.listClients();
      return response.data.data as User[];
    },
  });

  const handleEditClient = (client: User) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
  };

  const handleImpersonate = async (client: User) => {
    try {
      await impersonate(client.id);
      toast.success('Sucesso', `Visualizando dashboard de ${client.name}`);
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao acessar dashboard do cliente'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    }
  };

  // Filter by search query
  const clients = useMemo(() => {
    if (!data) return [];

    // Backend already filters only CLIENT role users
    let filtered = data;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          client.name.toLowerCase().includes(query) || client.email.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [data, searchQuery]);

  // Paginate filtered clients
  const totalPages = Math.ceil(clients.length / pageSize);

  // Reset page when search changes
  const handleSearchChange = (newSearch: string) => {
    setSearchQuery(newSearch);
    setPage(1);
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive
      ? 'bg-green-500/10 text-green-500 border-green-500/20'
      : 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Clientes</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            Gerencie os clientes do sistema
          </p>
        </div>
      </div>

      <ClientModal isOpen={isModalOpen} onClose={handleCloseModal} client={selectedClient} />

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <CardTitle className="flex items-center space-x-2">
              <UsersIcon className="h-5 w-5" />
              <span>Lista de Clientes</span>
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Client Count */}
          <div className="text-sm text-muted-foreground">
            Total: {clients.length} {clients.length === 1 ? 'cliente' : 'clientes'}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 border rounded-lg animate-pulse"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="h-10 w-10 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-48 bg-muted rounded" />
                      <div className="h-3 w-64 bg-muted rounded" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-6 w-16 bg-muted rounded-full" />
                    <div className="h-8 w-8 bg-muted rounded" />
                    <div className="h-8 w-8 bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-red-500">Erro ao carregar clientes</p>
            </div>
          )}

          {clients.length === 0 && !isLoading && !error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">
                {searchQuery ? 'Nenhum cliente encontrado com os filtros aplicados' : 'Nenhum cliente encontrado'}
              </p>
            </div>
          )}

          {clients.length > 0 && (
            <>
              <div className="-mx-4 sm:mx-0">
                <SortableTable data={clients}>
                  {(sortedData) => {
                    const paginatedData = sortedData.slice((page - 1) * pageSize, page * pageSize);
                    return (
                      <Table className="min-w-[640px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead
                              sortable
                              sortKey="name"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm"
                            >
                              Nome
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="email"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden md:table-cell"
                            >
                              Email
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="isActive"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden lg:table-cell"
                            >
                              Status
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="createdAt"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden xl:table-cell"
                            >
                              Criado em
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="lastAccessAt"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden xl:table-cell"
                            >
                              Último Acesso
                            </TableHead>
                            <TableHead className="text-right py-3 px-2 sm:px-4 text-xs sm:text-sm">
                              Ações
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedData.map((client) => (
                            <TableRow key={client.id}>
                              <TableCell className="py-3 px-2 sm:px-4">
                                <div className="font-medium text-foreground text-sm">
                                  {client.name}
                                </div>
                                <div className="text-xs text-muted-foreground md:hidden">
                                  {client.email}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden md:table-cell">
                                <div className="text-sm text-muted-foreground">{client.email}</div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden lg:table-cell">
                                <Badge className={getStatusBadgeColor(client.isActive)}>
                                  {client.isActive ? 'Ativo' : 'Inativo'}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden xl:table-cell">
                                <div className="text-sm text-muted-foreground">
                                  {formatDate(client.createdAt)}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden xl:table-cell">
                                <div className="text-sm text-muted-foreground">
                                  {client.lastAccessAt ? formatDate(client.lastAccessAt) : 'Nunca'}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4">
                                <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleImpersonate(client)}
                                    className="h-8 w-8 p-0"
                                    title="Ver Dashboard do Cliente"
                                  >
                                    <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleEditClient(client)}
                                    className="h-8 w-8 p-0"
                                    title="Editar Cliente"
                                  >
                                    <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    );
                  }}
                </SortableTable>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={clients.length}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
